import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { usePageTracking } from "./hooks/usePageTracking";

// Components
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import NewsletterPopup from "./components/NewsletterPopup";

// Public Pages
import Home from "./pages/Home";
import Rallies from "./pages/Rallies";
import RallyDetail from "./pages/RallyDetail";
import RallyRegister from "./pages/RallyRegister";
import Gallery from "./pages/Gallery";
import Sponsors from "./pages/Sponsors";
import Mission from "./pages/Mission";
import FAQ from "./pages/FAQ";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ThankYou from "./pages/ThankYou";

// Member Account Pages
import AccountLayout from "./pages/account/AccountLayout";
import AccountProfile from "./pages/account/AccountProfile";
import AccountVehicles from "./pages/account/AccountVehicles";
import AccountRegistrations from "./pages/account/AccountRegistrations";
import AccountPayments from "./pages/account/AccountPayments";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminRallies from "./pages/admin/AdminRallies";
import AdminSponsors from "./pages/admin/AdminSponsors";
import AdminSignups from "./pages/admin/AdminSignups";
import AdminInquiries from "./pages/admin/AdminInquiries";
import AdminNotify from "./pages/admin/AdminNotify";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminFAQs from "./pages/admin/AdminFAQs";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminMembers from "./pages/admin/AdminMembers";
import AdminVehicles from "./pages/admin/AdminVehicles";

// Analytics wrapper component
const AnalyticsWrapper = ({ children }) => {
  usePageTracking();
  return children;
};

// Public layout wrapper
const PublicLayout = ({ children }) => (
  <>
    <Navigation />
    <main>{children}</main>
    <Footer />
    <NewsletterPopup />
  </>
);

function App() {
  return (
    <AuthProvider>
      <div className="App bg-[#0A0A0A] min-h-screen">
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: '#141414',
              color: '#F2EDE6',
              border: '1px solid #2A2A2A',
            },
          }}
        />
        <BrowserRouter>
          <AnalyticsWrapper>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/rallies" element={<PublicLayout><Rallies /></PublicLayout>} />
              <Route path="/rallies/:rallyId" element={<RallyDetail />} />
              <Route path="/rallies/:rallyId/register" element={<RallyRegister />} />
              <Route path="/gallery" element={<PublicLayout><Gallery /></PublicLayout>} />
              <Route path="/sponsors" element={<PublicLayout><Sponsors /></PublicLayout>} />
              <Route path="/mission" element={<PublicLayout><Mission /></PublicLayout>} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/thank-you" element={<ThankYou />} />

              {/* Member Account Routes */}
              <Route path="/account" element={<AccountLayout />}>
                <Route index element={<AccountProfile />} />
                <Route path="vehicles" element={<AccountVehicles />} />
                <Route path="registrations" element={<AccountRegistrations />} />
                <Route path="payments" element={<AccountPayments />} />
              </Route>

              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/*" element={<AdminLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="members" element={<AdminMembers />} />
                <Route path="vehicles" element={<AdminVehicles />} />
                <Route path="rallies" element={<AdminRallies />} />
                <Route path="sponsors" element={<AdminSponsors />} />
                <Route path="signups" element={<AdminSignups />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                <Route path="notify" element={<AdminNotify />} />
                <Route path="gallery" element={<AdminGallery />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
                <Route path="faqs" element={<AdminFAQs />} />
                <Route path="payments" element={<AdminPayments />} />
              </Route>
            </Routes>
          </AnalyticsWrapper>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
